// /create (docs/08-app-design.md 5.1; app/art/welcome/PLAN.md sections 4.4
// and 5): explain once, show the phrase blurred, confirm three positions,
// set the device passphrase, then hand the unlocked worker to the session.
// The phrase is generated inside the worker; it crosses to this screen only
// to be shown, is never stored, and is dropped from memory the moment the
// passphrase step begins. Stops 4 and 5 (the fee reserve, Activate) live on
// /vault, which shows them for any vault that does not yet exist on chain.
// The ceremony frame carries the rail (explain and phrase are stop 1,
// confirm 2, passphrase 3); from the phrase on, the way back is Cancel,
// which discards the words (the worker ends on unmount), and the header
// keeps no link that could discard them silently. Browser Back during the
// save abandons it, as Cancel would, unless installSetup has already begun.
// Once the setup is saved, Cancel goes; a failure after that says the setup
// is saved and opens it with the passphrase just set, never offering to
// discard it (R67).

import { type WorkerClient, WorkerClientError, workerFeeSigner } from '@thevault/sdk';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { SUGGESTED_FUNDING_SOL, CeremonyFrame, LOCKED_WHILE_WAITING, SAVED_SETUP_UNREAD, type SavedNext, SetupSaved, SetupSaving, markSetupNotSaved, markSetupSavedAfter, markSetupSaving, reopenSaved, savedStepError, useSavedSetup } from '../components/Ceremony';
import { PassphraseForm } from '../components/Passphrase';
import { Notice, StatusLine } from '../components/primitives';
import { type VaultInfo, vaultInfoFromGenesis } from '../engine';
import { type RelayerSigner, toBase58 } from '../rpc';
import { FeeKeyUnsupported, PROGRAM_ID, PROGRAM_ID_B58, session, useSession } from '../session';
import { ONE_TIME_KEYS_PER_TREE } from '../site/facts';
import { installSetup } from '../storage';

// 'saved': the setup is saved on this device, then the session's adoption of it failed (R67; SetupSaved).
type Step = 'explain' | 'phrase' | 'confirm' | 'passphrase' | 'saved';

/** The stop each step is on the five-stop rail (StepRail's create flow). */
const STOP: Record<Step, number> = { explain: 1, phrase: 1, confirm: 2, passphrase: 3, saved: 3 };

/**
 * The worker's capability probe (docs/18-one-fee-reserve.md 2.4, "Browser support"), run before anything is stored:
 * a browser that cannot hold the fee key stops here with the one sentence unlock also uses. Restore runs it too.
 */
export async function probeFeeKey(client: WorkerClient): Promise<void> {
  try {
    await client.feeProbe();
  } catch (e) {
    if (e instanceof WorkerClientError && e.code === 'FeeKeyUnsupported') throw new FeeKeyUnsupported(e.message);
    throw e;
  }
}

function pickPositions(): number[] {
  const out = new Set<number>();
  const rnd = new Uint32Array(8);
  crypto.getRandomValues(rnd);
  for (const r of rnd) {
    out.add(r % 24);
    if (out.size === 3) break;
  }
  while (out.size < 3) out.add(out.size);
  return [...out].sort((a, b) => a - b);
}

export function Create() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>('explain');
  const [words, setWords] = useState<string[] | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [positions, setPositions] = useState<number[]>([]);
  const [typed, setTyped] = useState(['', '', '']);
  const [mismatch, setMismatch] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runtime = useRef<{ worker: Worker; client: WorkerClient } | null>(null);
  const adopted = useRef(false);
  // R67: whether the setup is saved on this device (installSetup resolved), kept apart from the step shown and from
  // `busy`. From then on nothing is left to discard and nothing may save again: Cancel goes, `finish` returns at
  // once, and a failure after it lands on SetupSaved, whose one way forward opens this same setup.
  const saved = useRef(false);
  const [committed, setCommitted] = useState(false);
  const [postSave, setPostSave] = useState<{ error: string; next: SavedNext } | null>(null);
  /** The session re-reading storage after a failed adoption, so /locked finds the saved setup. */
  const relock = useRef<Promise<void> | null>(null);
  const { state: sessionState } = useSession();
  // A save another mount of this page load started (it is committing, or the session is opening it), or a setup one
  // saved that the session could not read back: no fresh start here (savedSetup.ts).
  const other = useSavedSetup();

  // A save in flight keeps its worker even if the page unmounts (browser Back is not held off by `busy`), so its own
  // awaits settle; it then checks `mounted` just before installSetup and, if the page has gone, abandons the save
  // there, as Cancel would, and ends the worker itself. Past that check the save commits and the session adopts the
  // live worker (`adopted`).
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (!adopted.current && !saving.current) runtime.current?.worker.terminate();
    };
  }, []);

  const startPhrase = async () => {
    setBusy(true);
    setError(null);
    try {
      runtime.current ??= session.freshWorker();
      const mnemonic = await runtime.current.client.generate();
      setWords(mnemonic.split(' '));
      setRevealed(false);
      setStep('phrase');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toConfirm = () => {
    setPositions(pickPositions());
    setTyped(['', '', '']);
    setMismatch(null);
    setStep('confirm');
  };

  const check = (e: FormEvent) => {
    e.preventDefault();
    if (!words) return;
    const wrong = positions.filter((p, i) => typed[i]!.trim().toLowerCase() !== words[p]);
    if (wrong.length) {
      setMismatch(`The words you typed do not match position${wrong.length > 1 ? 's' : ''} ${wrong.map((p) => p + 1).join(', ')}. Read the phrase again.`);
      setRevealed(false);
      setStep('phrase');
      return;
    }
    setWords(null); // the phrase is on paper now; nothing here needs it
    setStep('passphrase');
  };

  const finish = async (passphrase: string) => {
    if (saved.current || saving.current || !runtime.current) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    const { client, worker } = runtime.current;
    let info: VaultInfo;
    let relayer: RelayerSigner;
    let storing = false;
    try {
      // docs/18 section 4: the probe first, so a browser that cannot hold the fee key stores nothing and the form stays.
      await probeFeeKey(client);
      const sealed = await client.seal(passphrase);
      const genesis = await client.deriveHeldTree(PROGRAM_ID, 0);
      // The fee reserve is derived from the words in the worker (n = 0, docs/18 2.4); only its address is stored, as
      // display data for the locked screens (section 3). No seed is drawn and no fee key is sealed.
      const feeWallet = toBase58(await client.feeAddress(0));
      // The payer is installed here, before installSetup, not after it: adoptUnlocked takes its I3 token when called
      // (session.ts), so no await may sit between the save and the adoption, or a lock or forget in that gap would
      // go unseen. The installed key must be the one whose address is stored.
      const derived = await client.activateFeePayer(0);
      if (toBase58(derived) !== feeWallet) throw new Error('The fee reserve key does not match its derived address. Nothing was saved.');
      relayer = workerFeeSigner(client, derived);
      info = { ...(await vaultInfoFromGenesis(PROGRAM_ID_B58, genesis.verifyingKey)), feeWallet };
      if (!mounted.current) {
        // The page went while the save ran (browser Back): leaving abandons the save, as Cancel does. Nothing is
        // stored, so a page reached meanwhile (Welcome, or this flow entered afresh) stays right; end the worker.
        saving.current = false;
        worker.terminate();
        if (runtime.current?.worker === worker) runtime.current = null;
        return;
      }
      // From here the save is not abandoned: other screens show it as saving until the session holds it (savedSetup.ts).
      storing = true;
      markSetupSaving();
      // One transaction, refused if this device already holds a setup (R48).
      await installSetup({ sealedMaster: sealed, vaultInfo: info });
    } catch (e) {
      saving.current = false;
      if (storing) markSetupNotSaved();
      if (!mounted.current) {
        // the page went while the save ran, and nothing was saved: end the worker the unmount left running
        worker.terminate();
        if (runtime.current?.worker === worker) runtime.current = null;
        return;
      }
      // nothing is saved: the form stays, with Cancel
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      return;
    }
    // Saved. The session takes this worker next, so unmounting must no longer end it.
    saved.current = true;
    adopted.current = true;
    saving.current = false;
    setCommitted(true);
    try {
      // No await since installSetup resolved (I3); the worker already holds the fee reserve as its payer (above).
      await session.adoptUnlocked(client, worker, relayer, info);
    } catch (e) {
      afterSaveFailed(savedStepError(e, 'This page could not open the saved setup.'));
      setBusy(false);
      return;
    }
    // The page went while installSetup ran (browser Back after the last abandon point): the session holds the setup
    // now; Welcome, and a /create or /restore entered meanwhile, move on to /vault themselves.
    if (!mounted.current) return;
    // /vault names the rail's stops after the flow that handed over (Vault.tsx, SetupProgress)
    navigate('/vault', { state: { setupFlow: 'create' } });
  };

  /** The adoption failed, or the app locked while the saved screen waited (R67): say so, and offer the way on. */
  const afterSaveFailed = (error: string) => {
    if (session.get().state === 'unlocked') {
      setPostSave({ error, next: 'continue' });
    } else {
      // The session does not hold the keys: end this page's copy of them, and let the session re-read storage,
      // where the sealed setup now is, so /locked opens it.
      // The setup is stored but the session no longer holds it: guard the other screens until the re-read reaches
      // 'locked', so none offers a fresh start over the stored setup. After a failed adoption 'saving' holds while the
      // read runs if the user has left this page, and 'unread' is armed only once it has failed; with this page still
      // shown it is armed at once, so the pill agrees with the saved screen (savedSetup.ts markSetupSavedAfter).
      adopted.current = false;
      // the session ends a worker it adopted, after its own wipe; this ends one it never took (R68, session.discardWorker)
      if (runtime.current) session.discardWorker(runtime.current.worker);
      runtime.current = null;
      relock.current = markSetupSavedAfter(session.lock(), mounted.current);
      setPostSave({ error, next: 'unlock' });
    }
    setStep('saved');
  };

  // The app locks itself (idle, or a hidden tab) while the saved screen offers Continue: say so, and offer Unlock.
  useEffect(() => {
    if (step !== 'saved' || busy || postSave?.next !== 'continue' || sessionState === 'unlocked' || sessionState === 'starting') return;
    afterSaveFailed(LOCKED_WHILE_WAITING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, busy, postSave, sessionState]);

  const resume = async () => {
    if (!postSave) return;
    if (postSave.next === 'continue' && session.get().state === 'unlocked') return navigate('/vault', { state: { setupFlow: 'create' } });
    setBusy(true);
    // Never to /welcome or /locked as if the device were empty: if the session cannot read the saved setup back, stay
    // here and offer a full reload (SavedNext 'reload').
    const where = await reopenSaved(relock.current);
    if (where === 'unread') {
      setPostSave({ error: SAVED_SETUP_UNREAD, next: 'reload' });
      setBusy(false);
      return;
    }
    navigate(where === 'locked' ? '/locked' : '/vault');
  };

  // This mount was let in while the device was empty (App.tsx RequireNoVault keeps rendering it after that) and has
  // saved nothing of its own, yet the session now holds or has read a setup: a save another mount of this page load
  // committed. Move on as the route guard would on entry, rather than run a fresh flow over the stored setup.
  useEffect(() => {
    if (saving.current || saved.current) return;
    if (sessionState === 'unlocked') navigate('/vault', { replace: true });
    else if (sessionState === 'locked') navigate('/welcome', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState]);

  // another mount's save, still committing or being opened; or a setup saved and not read back (R67's double fault)
  const elsewhere = other === 'saving' ? !saving.current && !saved.current : other === 'unread' && !committed;

  if (elsewhere) {
    return (
      <CeremonyFrame back={null} flow="create" step={other === 'saving' ? 'saving' : 'unread'} current={STOP.saved} secret>
        {other === 'saving' ? <SetupSaving /> : <SetupSaved error={SAVED_SETUP_UNREAD} next="reload" busy={false} />}
      </CeremonyFrame>
    );
  }

  return (
    <CeremonyFrame
      // from the moment the setup is saved there is nothing to discard and no way back (R67)
      back={committed ? null : step === 'explain' ? 'welcome' : 'cancel'}
      flow="create"
      step={step}
      current={STOP[step]}
      wide={step === 'phrase' || step === 'confirm'}
      // the saved screen holds no secret, but while the session reports an empty device (its re-read of the saved setup
      // has not finished, or failed) the header's links would lead to a fresh Welcome; they come back once it reads it
      secret={step === 'saved' ? sessionState !== 'locked' && sessionState !== 'unlocked' : step !== 'explain'}
      busy={busy && step === 'passphrase'}
      after={
        step === 'explain' ? (
          <p className="xlink">
            Already have a vault? <Link href="/restore">Restore it from your 24 words.</Link>
          </p>
        ) : null
      }
    >
      {step === 'explain' ? (
        <>
          <h1>Before you begin.</h1>
          <p className="lede">You are about to get 24 words. They are the only way into your vault: whoever holds them holds the vault, and nobody, this app included, can recover them for you.</p>
          <p className="b">
            Each action signs with one of {ONE_TIME_KEYS_PER_TREE} one-time keys. The app keeps count and refreshes the key on its own. <b>Your part: use the vault on one device at a time.</b>
          </p>
          <section className="kit" aria-labelledby="kit-title">
            <h2 id="kit-title">You will need</h2>
            <ul>
              <li>
                <p>
                  Paper and a pen. <span>Never a screenshot or a file.</span>
                </p>
              </li>
              <li>
                <p>
                  A private place. <span>The words appear only after you press Reveal.</span>
                </p>
              </li>
              <li>
                <p>
                  About {SUGGESTED_FUNDING_SOL} SOL in another wallet, for step 4; not needed yet.{' '}
                  <span>It funds the fee reserve, an ordinary Solana key, not quantum-resistant, that pays network fees and rent. It cannot move the vault&rsquo;s funds, and your 24 words restore it with the vault.</span>
                </p>
              </li>
            </ul>
          </section>
          {error ? (
            <StatusLine state="failed" word="Not started.">
              {error}
            </StatusLine>
          ) : null}
          <div className="entry-actions">
            <button type="button" className="cta cta-mint" onClick={() => void startPhrase()} disabled={busy} aria-busy={busy ? 'true' : undefined}>
              {busy ? 'Generating' : 'Continue'}
            </button>
            <Link href="/welcome" className="cta-quiet">
              Cancel
            </Link>
          </div>
        </>
      ) : null}

      {step === 'phrase' && words ? (
        <>
          <h1>Your 24 words.</h1>
          {mismatch ? (
            <StatusLine state="failed" word="Not confirmed.">
              {mismatch}
            </StatusLine>
          ) : null}
          <Notice label={revealed ? 'Write these on paper' : 'Hidden until you reveal them'} tone={revealed ? 'fail' : undefined}>
            <p className="ink">{revealed ? 'Write these on paper. Do not screenshot. Anyone with these words owns this vault. Never enter them into another wallet or website.' : 'Make sure nobody can see your screen, then reveal the phrase.'}</p>
          </Notice>
          <div className={`phrase ${revealed ? '' : 'blurred'}`.trim()} aria-hidden={!revealed}>
            {words.map((w, i) => (
              <div className="word" key={i}>
                <span>{i + 1}</span>
                <span>{revealed ? w : '········'}</span>
              </div>
            ))}
          </div>
          <div className="entry-actions">
            {!revealed ? (
              <button type="button" className="cta cta-mint" onClick={() => setRevealed(true)}>
                Reveal
              </button>
            ) : (
              <button type="button" className="cta cta-mint" onClick={toConfirm}>
                I have written them down
              </button>
            )}
          </div>
        </>
      ) : null}

      {step === 'confirm' ? (
        <form onSubmit={check} className="stack">
          <h1>Confirm three words.</h1>
          <p className="lede">Type the words at these positions from your paper copy. Any mistake returns you to the phrase.</p>
          <div className="confirm">
            {positions.map((p, i) => (
              <div className="field" key={p}>
                <label htmlFor={`confirm-${p}`}>Word {p + 1}</label>
                <input id={`confirm-${p}`} className="input" autoComplete="off" autoCapitalize="none" spellCheck={false} value={typed[i]} onChange={(e) => setTyped((t) => t.map((v, j) => (j === i ? e.target.value : v)))} autoFocus={i === 0} />
              </div>
            ))}
          </div>
          <div className="entry-actions">
            <button type="submit" className="cta cta-mint" disabled={typed.some((t) => !t.trim())}>
              Check
            </button>
          </div>
        </form>
      ) : null}

      {step === 'passphrase' ? (
        <>
          <h1>A passphrase for this device.</h1>
          <PassphraseForm onSubmit={(p) => void finish(p)} busy={busy} error={error} submitLabel="Create the vault" />
        </>
      ) : null}

      {step === 'saved' && postSave ? <SetupSaved error={postSave.error} next={postSave.next} busy={busy} label={postSave.next === 'continue' ? 'opening the vault on this page' : undefined} onNext={() => void resume()} /> : null}
    </CeremonyFrame>
  );
}
